import { z } from "zod";

/** The fixed buyer-facing email template types; each has a seeded template + typed context (custom types deferred). */
export const emailTemplateTypeSchema = z.enum([
  "order.confirmation",
  "order.fulfillment",
  "order.refund",
]);
export type EmailTemplateType = z.infer<typeof emailTemplateTypeSchema>;

export const EMAIL_TEMPLATE_TYPES = emailTemplateTypeSchema.options;

// NOTE (Yuxin, 2026-07-22): transactional types legally need no unsubscribe
// link; the shell and List-Unsubscribe header include one only for these.
// NOTE (Max, 2026-07-31): empty until the first marketing type lands — the
// classification is kept wired rather than deleted because merchant marketing
// email follows the Customer keystone (#23409) within weeks (REPL-30123).
export const MARKETING_EMAIL_TYPES: readonly EmailTemplateType[] = [];
export function isMarketingEmailType(type: EmailTemplateType): boolean {
  return MARKETING_EMAIL_TYPES.includes(type);
}

/** One compiled version: the send-ready artifact. `subject`/`preheader`/`html`/`text` all carry embedded Liquid, filled at send. `html` is the FULL shelled document (compliance shell injected server-side). `sourceTsx` is the agent-authored source, versioned alongside the artifact so rollback restores both. */
export const compiledEmailVersionSchema = z.object({
  subject: z.string(),
  preheader: z.string(),
  html: z.string(),
  text: z.string(),
  sourceTsx: z.string(),
  sourceHash: z.string(),
});
export type CompiledEmailVersion = z.infer<typeof compiledEmailVersionSchema>;

/** Canonical on-disk doc per type (`.email/<type>.email.json`): the caller's
 * authoring workbench — their working draft only. The published artifact is
 * project-owned and lives in `agent_email_template_version`. */
export const emailTemplateDocSchema = z.object({
  type: emailTemplateTypeSchema,
  draft: compiledEmailVersionSchema,
  updatedAt: z.string(),
});
export type EmailTemplateDoc = z.infer<typeof emailTemplateDocSchema>;
